// Steps 1 + 2 of the availability panel: WHAT and HOW MANY, on the option
// cards themselves (the shared UnitCard, never a copy).
//
//  sessions  one card is picked; its stepper is the visit's headcount.
//  multi     several cards are ticked, each with its own count, up to the
//            band RPC's entry cap.
//
// A variable-length option (priced per length, no fixed length) chooses its
// length on its own card — the same per-card model the staff app and the bot
// use. Per-item option-led options are not offered in this build: they render
// "Not offered" (the parent logs why).

import type { WidgetService, WidgetUnit } from "../../types";
import { optionWord } from "../../state/next-step-logic";
import { UnitCard } from "../booking/UnitCard";
import { HScroller } from "./HScroller";

/**
 * Below this width of the card ROW (not the window), one card shows at a
 * time, at the row's full width, with a pager (HScroller `paged`) — owner,
 * 25 Sept: "I would prefer one card on screen at a time on mobile". A phone
 * (a 280–370 px row) and the owner's ~480 px window (a ~365 px row) page;
 * from a tablet's 768 px window (a ~620 px row) the row keeps its several
 * cards and the peek. Read from the row, so a widget embedded in a narrow
 * column of a wide page pages too.
 */
export const ONE_CARD_BELOW_PX = 560;

export type WhatStepProps = {
  service: WidgetService;
  shape: "sessions" | "multi";
  noun: { one: string; other: string };
  units: WidgetUnit[];
  offered: (unit: WidgetUnit) => boolean;
  sessionUnitId: string | null;
  /** A ticked option to bring into view while it stays ticked (a ?option=
   *  link's). A session's pick is always brought into view. */
  revealUnitId?: string | null;
  headcount: number;
  counts: Readonly<Record<string, number>>;
  /** No more options can be ticked (the entry cap). */
  tickCapReached: boolean;
  maxEntries: number;
  capFor: (unit: WidgetUnit) => number;
  /** The fewest guests one booking of the option takes (1: no floor). The
   *  stepper stops there and the card reads "2–4 riders". */
  minFor?: (unit: WidgetUnit) => number;
  /** The ceiling shown on the card ("Up to 8 · 4 per unit", or a "block"
   *  limit below the unit's size) — the same number the stepper stops at, for
   *  a session and a ticked option alike (null: the card shows the unit's size). */
  partyMaxFor: (unit: WidgetUnit) => number | null;
  lengthFor: (unit: WidgetUnit) => number;
  lengthChoices: (unit: WidgetUnit) => number[];
  formatLength: (minutes: number) => string;
  displayPrice: (unit: WidgetUnit) => number | null;
  /** Prices are switched off for this service — no money on the cards. */
  hidePrice?: boolean;
  priceSuffix: (unit: WidgetUnit) => string;
  priceFailed: (unit: WidgetUnit) => boolean;
  onPriceRetry: () => void;
  disabled: boolean;
  onPickSession: (unit: WidgetUnit) => void;
  onHeadcount: (n: number) => void;
  onToggle: (unit: WidgetUnit) => void;
  onCount: (unit: WidgetUnit, n: number) => void;
  onLength: (unit: WidgetUnit, minutes: number) => void;
};

export function WhatStep(p: WhatStepProps) {
  const heroPlaceholder = p.units.some((u) => u.image_url);
  const stepperName = p.noun.other.charAt(0).toUpperCase() + p.noun.other.slice(1);
  const word = optionWord(p.shape, p.noun);
  const isChosen = (unit: WidgetUnit | undefined) =>
    !!unit && (p.shape === "sessions" ? unit.id === p.sessionUnitId : (p.counts[unit.id] ?? 0) > 0);
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
  return (
    <HScroller
      className="vt-avail-option-row"
      // "Show more options" names each day's extra start times (DayStrip);
      // the arrows say which way along the row they go.
      prevLabel={`Show earlier ${word.other}`}
      nextLabel={`Show later ${word.other}`}
      revealSelector=".vt-option-selected"
      revealKey={
        p.shape === "sessions"
          ? p.sessionUnitId
          : p.revealUnitId && (p.counts[p.revealUnitId] ?? 0) > 0
            ? p.revealUnitId
            : null
      }
      paged={{
        below: ONE_CARD_BELOW_PX,
        prevLabel: `Previous ${word.one}`,
        nextLabel: `Next ${word.one}`,
        groupLabel: cap(word.other),
        itemLabel: (i) => p.units[i]?.name ?? "",
        itemChosen: (i) => isChosen(p.units[i]),
      }}
    >
      <div className="vt-option-cards vt-avail-options">
        {p.units.map((unit) => {
          const offered = p.offered(unit);
          const choices = p.lengthChoices(unit);
          const variable = choices.length > 0;
          const selected =
            p.shape === "sessions" ? unit.id === p.sessionUnitId : (p.counts[unit.id] ?? 0) > 0;
          const capped = p.shape === "multi" && !selected && p.tickCapReached;
          const unavailable = !offered || capped;
          const cap = p.capFor(unit);
          // A floor above the ceiling is not offered at all (the panel greys it).
          const min = p.minFor?.(unit) ?? 1;
          return (
            <UnitCard
              key={unit.id}
              unit={unit}
              heroPlaceholder={heroPlaceholder}
              payAtVenue={p.service.pay_at_venue === true}
              priceSuffix={p.hidePrice ? "" : p.priceSuffix(unit)}
              displayPrice={p.displayPrice(unit)}
              hidePrice={p.hidePrice}
              priceLabel={offered && p.priceFailed(unit) ? "Price unavailable" : undefined}
              onPriceRetry={offered && p.priceFailed(unit) ? p.onPriceRetry : undefined}
              unavailable={unavailable}
              unavailableLabel={
                !offered ? "Not offered" : capped ? `Up to ${p.maxEntries} options per visit` : undefined
              }
              metaPrefix={variable ? undefined : p.formatLength(p.lengthFor(unit))}
              duration={
                variable && offered
                  ? {
                      minutes: p.lengthFor(unit),
                      choices,
                      disabled: p.disabled,
                      onChange: (m) => p.onLength(unit, m),
                      format: p.formatLength,
                    }
                  : undefined
              }
              selected={selected}
              onSelect={() => {
                if (p.disabled) return;
                if (p.shape === "sessions") p.onPickSession(unit);
                else p.onToggle(unit);
              }}
              multiSelect={p.shape === "multi"}
              stepperWhenSelected={p.shape === "sessions"}
              partyMax={p.partyMaxFor(unit)}
              quantity={p.shape === "sessions" ? p.headcount : (p.counts[unit.id] ?? 0)}
              onQuantityChange={(n) => {
                if (p.disabled) return;
                const next = Math.max(Math.min(min, cap), Math.min(cap, Math.round(n)));
                if (p.shape === "sessions") p.onHeadcount(next);
                else p.onCount(unit, next);
              }}
              qtyLabel={p.noun.other}
              stepperAriaLabel={`${stepperName} on ${unit.name}`}
              // At the cap "+" stays focusable (aria-disabled) so a keyboard
              // guest is not dropped to <body> — as the option cards always did.
              stepperRetainFocus
              selectLabels={{ idle: "Choose", selected: "Chosen ✓" }}
              maxQuantity={cap}
              minQuantity={min}
              partyNoun={p.noun}
            />
          );
        })}
      </div>
    </HScroller>
  );
}
